import { useState } from "react";
import { useParams } from "react-router-dom";
import jsPDF from "jspdf";
import api from "../lib/api";
import { ShieldCheck, Thermometer, Truck, Boxes, FileText, Download, Phone, Mail, MapPin, X } from "lucide-react";

const HACCP_LABEL = { fridge: "Frigoriferi / Temperature", fridges: "Frigoriferi / Temperature", cleaning: "Pulizie / Sanificazioni", pests: "Lotta agli infestanti", pest: "Lotta agli infestanti" };
const CONSERV = { frigo: "Frigo (0–4°C)", ambiente: "Ambiente", congelatore: "Congelatore (−18°C)", abbattitore: "Abbattuto" };

const Tab = ({ id, active, setActive, icon: Icon, label }) => (
  <button onClick={() => setActive(id)} data-testid={`insp-tab-${id}`}
    className={`flex items-center gap-2 px-4 py-2 text-sm rounded-full whitespace-nowrap transition-colors ${active === id ? "bg-[#c24127] text-white" : "bg-white text-neutral-600 border border-neutral-200"}`}>
    <Icon className="w-4 h-4" /> {label}
  </button>
);

const haccpDetails = (h) => {
  const parts = [];
  if (h.location) parts.push(h.location);
  if (h.temperature !== undefined && h.temperature !== null && h.temperature !== "") parts.push(`${h.temperature}°C`);
  if (h.value) parts.push(String(h.value));
  if (h.status) parts.push(h.status);
  if (h.done !== undefined) parts.push(h.done ? "Eseguito" : "Non eseguito");
  if (h.note || h.notes) parts.push(h.note || h.notes);
  if (h.operator || h.user) parts.push(`— ${h.operator || h.user}`);
  return parts.join(" · ") || "—";
};

export default function InspectionPortal() {
  const { token } = useParams();
  const [code, setCode] = useState("");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [authErr, setAuthErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState("haccp");
  const [supDetail, setSupDetail] = useState(null);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!code.trim()) return;
    setLoading(true); setAuthErr("");
    try {
      const { data } = await api.post(`/public/inspection/${token}`, { code: code.trim() });
      setD(data);
    } catch (e2) {
      if (e2?.response?.status === 404) setErr(true);
      else setAuthErr("Password non valida. Chiedi al titolare il codice aggiornato.");
    } finally { setLoading(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-neutral-500 p-6 text-center">Link ispezione non valido o scaduto.</div>;
  if (!d) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-100 p-4" data-testid="inspection-gate">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6 w-full max-w-sm text-center">
        <ShieldCheck className="w-10 h-10 text-[#c24127] mx-auto mb-3" />
        <h1 className="text-lg font-semibold">Accesso controllo ispettivo</h1>
        <p className="text-sm text-neutral-500 mt-1 mb-4">Inserisci la password fornita dal titolare.</p>
        <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="Password" data-testid="inspection-code-input"
          className="w-full text-center tracking-[0.3em] text-lg border border-neutral-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c24127]" />
        {authErr && <p className="text-sm text-red-600 mt-2" data-testid="inspection-auth-error">{authErr}</p>}
        <button type="submit" disabled={loading} data-testid="inspection-submit" className="mt-4 w-full bg-[#c24127] text-white rounded-xl py-2 font-medium disabled:opacity-60">{loading ? "Verifica…" : "Accedi"}</button>
      </form>
    </div>
  );

  const Th = ({ children }) => <th className="text-left px-3 py-2 font-medium">{children}</th>;
  const Td = ({ children }) => <td className="px-3 py-2 border-t border-neutral-100">{children}</td>;
  const Section = ({ children }) => <div className="rounded-xl border border-neutral-200 overflow-x-auto bg-white"><table className="w-full text-sm">{children}</table></div>;

  const TITLES = { haccp: "Registri HACCP", suppliers: "Elenco Fornitori", batches: "Tracciabilità merci ricevute", sheets: "Schede prodotto" };
  const exportPdf = (section) => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const biz = d.business || {};
    let y = 16;
    doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.text(biz.name || "Dogana 2.0", 14, y);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); y += 5;
    if (biz.address) { doc.text(String(biz.address), 14, y); y += 4; }
    if (biz.vat) { doc.text(`P.IVA: ${biz.vat}`, 14, y); y += 4; }
    doc.setDrawColor(180); doc.line(14, y, 196, y); y += 8;
    doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(194, 65, 39);
    doc.text(TITLES[section], 14, y); doc.setTextColor(26); y += 8;
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    const put = (txt) => { if (y > 285) { doc.addPage(); y = 16; } doc.text(String(txt).slice(0, 125), 14, y); y += 5; };
    if (section === "haccp") { d.haccp.forEach((h) => put(`${h.date || "—"}  ·  ${HACCP_LABEL[h.register] || h.register || h.type || ""}  ·  ${haccpDetails(h)}`)); if (!d.haccp.length) put("Nessun registro."); }
    else if (section === "suppliers") { d.suppliers.forEach((s) => put(`${s.name || "—"}  ·  ${[s.phone, s.email].filter(Boolean).join(" · ") || "—"}  ·  P.IVA ${s.vat || "—"}  ·  ${s.address || ""}`)); if (!d.suppliers.length) put("Nessun fornitore."); }
    else if (section === "batches") { d.batches.forEach((b) => put(`${b.product || "—"}  ·  L.${b.lotto || "—"}  ·  ric.${b.received_date || "—"}  ·  ${b.supplier || "—"}  ·  scad.${b.expiry || "—"}`)); if (!d.batches.length) put("Nessuna merce."); }
    else { d.product_sheets.forEach((s) => put(`${s.name || "—"}  ·  ${s.lot_number || ""}  ·  ${s.prep_date || ""}  ·  ${(s.allergens || []).join(", ") || "—"}`)); if (!d.product_sheets.length) put("Nessuna scheda."); }
    doc.setFontSize(7); doc.setTextColor(120);
    doc.text(`Generato il ${new Date().toLocaleString("it-IT")} · ${TITLES[section]}`, 14, 292);
    doc.save(`${section}-dogana.pdf`);
  };

  return (
    <div className="min-h-screen bg-neutral-100 pb-10" data-testid="inspection-portal">
      <div className="bg-[#c24127] text-white px-5 py-5">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-widest opacity-80">{d.business?.name || "Dogana 2.0"}</p>
          <h1 className="text-2xl font-semibold flex items-center gap-2 mt-1"><ShieldCheck className="w-6 h-6" /> Portale controllo ispettivo</h1>
          <p className="text-xs opacity-80 mt-1">Registri HACCP, fornitori e tracciabilità · aggiornato al {new Date(d.generated_at).toLocaleString("it-IT")}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4">
        <div className="flex gap-2 overflow-x-auto py-4 -mx-1 px-1">
          <Tab id="haccp" active={active} setActive={setActive} icon={Thermometer} label={`HACCP (${d.haccp.length})`} />
          <Tab id="suppliers" active={active} setActive={setActive} icon={Truck} label={`Fornitori (${d.suppliers.length})`} />
          <Tab id="batches" active={active} setActive={setActive} icon={Boxes} label={`Tracciabilità (${d.batches.length})`} />
          <Tab id="sheets" active={active} setActive={setActive} icon={FileText} label={`Schede (${d.product_sheets.length})`} />
        </div>

        <div className="flex justify-end mb-3">
          <button onClick={() => exportPdf(active)} data-testid="insp-download-pdf"
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-[#c24127] text-white">
            <Download className="w-4 h-4" /> Scarica PDF · {TITLES[active]}
          </button>
        </div>

        {active === "haccp" && (
          <Section>
            <thead className="bg-neutral-50 text-neutral-500 text-xs"><tr><Th>Data</Th><Th>Registro</Th><Th>Dettagli</Th></tr></thead>
            <tbody>
              {d.haccp.map((h) => <tr key={h.id}><Td>{h.date}</Td><Td>{HACCP_LABEL[h.register] || h.register || h.type}</Td><Td>{haccpDetails(h)}</Td></tr>)}
              {!d.haccp.length && <tr><Td>Nessun registro HACCP.</Td></tr>}
            </tbody>
          </Section>
        )}

        {active === "suppliers" && (
          <Section>
            <thead className="bg-neutral-50 text-neutral-500 text-xs"><tr><Th>Fornitore</Th><Th>Contatti</Th><Th>P.IVA</Th></tr></thead>
            <tbody>
              {d.suppliers.map((s) => <tr key={s.id} onClick={() => setSupDetail(s)} className="cursor-pointer hover:bg-neutral-50" data-testid={`insp-supplier-${s.id}`}><Td>{s.name}</Td><Td>{[s.phone, s.email].filter(Boolean).join(" · ") || "—"}</Td><Td>{s.vat || s.piva || "—"}</Td></tr>)}
              {!d.suppliers.length && <tr><Td>Nessun fornitore.</Td></tr>}
            </tbody>
          </Section>
        )}

        {active === "batches" && (
          <Section>
            <thead className="bg-neutral-50 text-neutral-500 text-xs"><tr><Th>Prodotto</Th><Th>Lotto</Th><Th>Ricevuto</Th><Th>Fornitore</Th><Th>Scadenza</Th></tr></thead>
            <tbody>
              {d.batches.map((b) => <tr key={b.id}><Td>{b.product}</Td><Td>{b.lotto || "—"}</Td><Td>{b.received_date}</Td><Td>{b.supplier || "—"}</Td><Td>{b.expiry || "—"}</Td></tr>)}
              {!d.batches.length && <tr><Td>Nessuna merce ricevuta.</Td></tr>}
            </tbody>
          </Section>
        )}

        {active === "sheets" && (
          <Section>
            <thead className="bg-neutral-50 text-neutral-500 text-xs"><tr><Th>Prodotto</Th><Th>Lotto interno</Th><Th>Prodotto il</Th><Th>Conservazione</Th><Th>Allergeni</Th></tr></thead>
            <tbody>
              {d.product_sheets.map((s) => <tr key={s.id}><Td>{s.name}</Td><Td>{s.lot_number}</Td><Td>{s.prep_date}</Td><Td>{CONSERV[s.conservation] || s.conservation}</Td><Td>{(s.allergens || []).join(", ") || "—"}</Td></tr>)}
              {!d.product_sheets.length && <tr><Td>Nessuna scheda prodotto.</Td></tr>}
            </tbody>
          </Section>
        )}

        {active === "suppliers" && (
          <p className="text-[11px] text-neutral-400 mt-2 text-center">Tocca un fornitore per vedere indirizzo e contatti completi.</p>
        )}

        <p className="text-[10px] text-neutral-400 mt-6 text-center leading-relaxed">{d.business?.address}{d.business?.vat ? " · P.IVA " + d.business.vat : ""}<br />Documentazione conforme a Reg. CE 178/2002, 852/2004 e Reg. UE 1169/2011.</p>
      </div>

      {supDetail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={() => setSupDetail(null)} data-testid="insp-supplier-detail">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSupDetail(null)} className="absolute top-4 right-4 text-neutral-400" data-testid="insp-supplier-close"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 mb-4"><Truck className="w-5 h-5 text-[#c24127]" /><h2 className="text-lg font-semibold">{supDetail.name}</h2></div>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-neutral-400 mt-0.5" /><span>{supDetail.address || "Indirizzo non disponibile"}</span></div>
              <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-neutral-400" />{supDetail.phone ? <a href={`tel:${supDetail.phone}`} className="text-[#c24127]">{supDetail.phone}</a> : <span className="text-neutral-400">—</span>}</div>
              <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-neutral-400" />{supDetail.email ? <a href={`mailto:${supDetail.email}`} className="text-[#c24127]">{supDetail.email}</a> : <span className="text-neutral-400">—</span>}</div>
              <div className="flex items-center gap-3"><FileText className="w-4 h-4 text-neutral-400" /><span>P.IVA / C.F.: {supDetail.vat || supDetail.piva || "—"}</span></div>
              <div className="pt-2 border-t border-neutral-100 text-xs text-neutral-500">Ordini registrati: {supDetail.orders || 0} · Ultima consegna: {supDetail.last_date || "—"}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
